import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { api, languageStore } from '@/api/client';
import { EN_FALLBACK, AR_FALLBACK } from './fallback';

/**
 * Strings live in the database so HR can correct wording without a deploy;
 * the bundled fallback covers the sign-in screen and any key added after the
 * last seed run.
 */
export async function initI18n(): Promise<typeof i18n> {
  const language = languageStore.get();

  await i18n.use(initReactI18next).init({
    lng: language,
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],
    defaultNS: 'common',
    ns: ['common'],
    // Keys are flat dotted strings ('setup.branch'), not nested paths.
    keySeparator: false,
    nsSeparator: false,
    resources: { en: { common: EN_FALLBACK }, ar: { common: AR_FALLBACK } },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  await loadRemoteBundle(language);
  return i18n;
}

/** Merges server translations over the bundled defaults. */
export async function loadRemoteBundle(language: string): Promise<void> {
  try {
    // Through the API client, so a deployment with its own API host
    // (VITE_API_BASE) is asked, and with the workspace header — wording is
    // per workspace, corrected by each customer's HR on the Wording page.
    const bundle = await api.get<Record<string, Record<string, string>>>(
      `/i18n/${encodeURIComponent(language)}`,
    );
    // The API groups by namespace for lazy loading; the client keeps one flat
    // table because every key already carries its prefix.
    const flat = Object.values(bundle).reduce<Record<string, string>>(
      (acc, entries) => Object.assign(acc, entries),
      {},
    );
    i18n.addResourceBundle(language, 'common', flat, true, true);
  } catch {
    // Offline start or a failed request: the bundled fallback carries the app.
  }
}

export async function changeLanguage(language: string): Promise<void> {
  languageStore.set(language);
  await loadRemoteBundle(language);
  await i18n.changeLanguage(language);
}

/**
 * Maps a rendered string back to the English it was translated from.
 *
 * Help copy is keyed by English labels and headers; once those render in
 * Arabic the lookup would miss, so the reverse of the Arabic bundle is kept
 * and rebuilt whenever the bundle changes.
 */
let reverse: Map<string, string> | null = null;
i18n.on('added', () => (reverse = null));
i18n.on('languageChanged', () => (reverse = null));
export function toEnglish(text: string): string {
  if (!reverse) {
    reverse = new Map();
    const bundle = (i18n.getResourceBundle('ar', 'common') ?? {}) as Record<string, string>;
    for (const [key, value] of Object.entries(bundle)) if (!reverse.has(value)) reverse.set(value, key);
  }
  const key = reverse.get(text);
  if (!key) return text;
  // A dotted base key maps to its English wording, which is what help copy is keyed by.
  const en = (i18n.getResourceBundle('en', 'common') ?? {}) as Record<string, string>;
  return en[key] ?? key;
}

export default i18n;
